import { useCallback, useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { isActive, pollDelayMs } from "./refreshStatus";
import type { FrsRefreshStatus } from "./types";

const TICK_MS = 1000;

export function useRefreshStatus() {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [status, setStatus] = useState<FrsRefreshStatus | null>(null);
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [unreachable, setUnreachable] = useState(false);
  const latest = useRef<FrsRefreshStatus | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const response = await apiFetchRef.current("facilities/refresh-status");
      const body: FrsRefreshStatus = await response.json();
      if (!alive.current) return;
      latest.current = body;
      setStatus(body);
      setFetchedAt(Date.now());
      setNow(Date.now());
      setUnreachable(false);
    } catch {
      if (alive.current) setUnreachable(true);
    }
  }, []);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      await load();
      if (alive.current) schedule();
    }, pollDelayMs(latest.current));
  }, [load]);

  const reload = useCallback(async () => {
    await load();
    if (alive.current) schedule();
  }, [load, schedule]);

  useEffect(() => {
    alive.current = true;
    void reload();
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [reload]);

  const active = isActive(status);
  useEffect(() => {
    if (!active) return;
    const ticker = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(ticker);
  }, [active]);

  const elapsedSeconds =
    status?.elapsedSeconds === null || status?.elapsedSeconds === undefined
      ? null
      : status.elapsedSeconds + (now - fetchedAt) / 1000;

  return { status, elapsedSeconds, unreachable, reload };
}
